/**
 * Shared Cell Renderer Utilities
 * Reusable rendering functions for DataGrid cells across all domains
 */

import {
  GridRenderCellParams,
  GridValidRowModel,
} from '@mui/x-data-grid-premium';
import React from 'react';
import ExpandableCell, {
  ExpandableListCell,
} from '../../components/ExpandableCell';
import {
  BaseFieldDefinition,
  RelationConfig,
  isDateOnlyField,
} from '../types/fieldRegistry';
import { COLOR_SUCCESS, COLOR_DANGER } from '../../theme/colors';
import { formatDateOnly, formatTimestamp } from '../../utils/dateConversion';

/**
 * Render a boolean value with styled checkmark or X
 */
export function renderBooleanCell(value: unknown): React.ReactElement | string {
  if (value === null || value === undefined) return '—';
  return value ? (
    <span style={{ color: COLOR_SUCCESS, fontWeight: 'bold' }}>✓</span>
  ) : (
    <span style={{ color: COLOR_DANGER, fontWeight: 'bold' }}>✗</span>
  );
}

/**
 * Render a selection value with label lookup
 */
export function renderSelectionCell(
  value: unknown,
  options: Array<{ value: string; label: string }>
): string {
  if (value === null || value === undefined) return '';
  const option = options.find(opt => opt.value === value);
  return option ? option.label : String(value);
}

/**
 * The LGM-retained QTA sentinel (issue #653, Task 13, Sub-task 13.2,
 * Decision 12): `9999-12-30` means "no expiration set," not a real date.
 * Checking the year alone is timezone-safe — `toLocaleDateString()` on a
 * value this far out never shifts across a year boundary the way a
 * near-term date could. Shared with `FacilityMergeConflictResolver.tsx`,
 * which needs the same check on the same field outside the grid.
 */
export function isQtaSentinelDate(value: Date): boolean {
  return value.getUTCFullYear() >= 9999;
}

/**
 * Render a UTC timestamp (created/modified, etc.) in the viewer's time zone,
 * with a zone label
 */
export function renderDateCell(value: unknown): string {
  return formatTimestamp(value);
}

/**
 * Render a date-only value (no time component in the source data) as the raw
 * calendar date -- never time-zone converted
 */
export function renderDateOnlyCell(value: unknown): string {
  return formatDateOnly(value);
}

/**
 * Same as `renderDateOnlyCell`, but renders the QTA "no expiration" sentinel
 * (see `isQtaSentinelDate`) as readable text instead of a literal
 * Dec 30, 9999 -- scoped to `qta_expiration_date` only (via `createCellRenderer`
 * below), not applied to every date field across every domain.
 */
export function renderQtaExpirationDateCell(value: unknown): string {
  if (!value) return '';
  const date = new Date(value as string | Date);
  if (isQtaSentinelDate(date)) return 'No expiration';
  return formatDateOnly(date);
}

/**
 * Render an array of strings with truncation
 */
export function renderStringArrayCell(
  items: string[],
  maxItems: number = 2,
  isHtml: boolean = false
): React.ReactElement {
  return (
    <ExpandableListCell
      items={items || []}
      maxItems={maxItems}
      isHtml={isHtml}
    />
  );
}

/**
 * Render a relational array (objects) with display field
 */
export function renderRelationArrayCell(
  items: Array<Record<string, unknown>>,
  relationConfig: RelationConfig,
  maxItems: number = 2
): React.ReactElement {
  const { displayField, preferredDisplayField, statusField } = relationConfig;

  // Special handling for relations with status field
  if (statusField) {
    const itemStrings: string[] = items.map(
      item => `${item[displayField]} | ${item[statusField] || 'N/A'}`
    );
    return <ExpandableListCell items={itemStrings} maxItems={maxItems} />;
  }

  // Handle preferred display field with fallback
  if (preferredDisplayField) {
    const itemStrings: string[] = items.map(
      item =>
        (item[preferredDisplayField] as string) ||
        (item[displayField] as string) ||
        'Unnamed'
    );
    return <ExpandableListCell items={itemStrings} maxItems={maxItems} />;
  }

  // Standard relational display (just the name)
  const itemStrings: string[] = items.map(
    item => (item[displayField] as string) || 'Unnamed'
  );
  return <ExpandableListCell items={itemStrings} maxItems={maxItems} />;
}

/**
 * Render HTML content with optional truncation
 */
export function renderHtmlCell(
  content: string,
  maxLength: number = 100
): React.ReactElement {
  return (
    <ExpandableCell content={content} isHtml={true} maxLength={maxLength} />
  );
}

/**
 * Render text content with optional truncation
 */
export function renderTextCell(
  content: string,
  maxLength?: number
): React.ReactElement | string {
  if (!maxLength) return content;
  return (
    <ExpandableCell content={content} isHtml={false} maxLength={maxLength} />
  );
}

/**
 * Create a cell renderer function based on field definition
 * Returns undefined for fields that don't need custom rendering
 */
export function createCellRenderer<TEntity extends GridValidRowModel>(
  field: BaseFieldDefinition<TEntity>
):
  | ((params: GridRenderCellParams<TEntity>) => React.ReactElement | string)
  | undefined {
  // Handle boolean fields
  if (field.dataType === 'boolean') {
    return (params: GridRenderCellParams<TEntity>) => {
      const value = params.row[field.key as keyof TEntity];
      return renderBooleanCell(value);
    };
  }

  // Handle selection fields
  if (field.dataType === 'selection' && field.selectionOptions) {
    return (params: GridRenderCellParams<TEntity>) => {
      const value = params.row[field.key as keyof TEntity];
      return renderSelectionCell(value, field.selectionOptions || []);
    };
  }

  // Handle date fields
  if (field.dataType === 'date') {
    const dateRenderer =
      field.key === 'qta_expiration_date'
        ? renderQtaExpirationDateCell
        : isDateOnlyField(field)
          ? renderDateOnlyCell
          : renderDateCell;
    return (params: GridRenderCellParams<TEntity>) => {
      const value = params.row[field.key as keyof TEntity];
      return dateRenderer(value);
    };
  }

  // Handle array of strings (non-relational)
  if (
    field.dataType === 'array' &&
    field.primitiveType === 'string' &&
    !field.isRelation
  ) {
    return (params: GridRenderCellParams<TEntity>) => {
      const items = (params.row[field.key as keyof TEntity] as string[]) || [];
      return renderStringArrayCell(
        items,
        field.renderConfig?.maxDisplayItems,
        field.renderConfig?.formatAs === 'html'
      );
    };
  }

  // Handle relational arrays
  const { relationConfig } = field;
  if (field.isRelation && relationConfig) {
    return (params: GridRenderCellParams<TEntity>) => {
      const items =
        (params.row[field.key as keyof TEntity] as unknown as Array<
          Record<string, unknown>
        >) || [];
      return renderRelationArrayCell(
        items,
        relationConfig,
        field.renderConfig?.maxDisplayItems
      );
    };
  }

  // Handle HTML content or truncation
  if (
    field.renderConfig?.formatAs === 'html' ||
    field.renderConfig?.maxDisplayLength
  ) {
    return (params: GridRenderCellParams<TEntity>) => {
      const value = params.row[field.key as keyof TEntity];
      if (!value) return '';

      if (field.renderConfig?.formatAs === 'html') {
        return renderHtmlCell(
          value as string,
          field.renderConfig?.maxDisplayLength
        );
      } else {
        return renderTextCell(
          value as string,
          field.renderConfig?.maxDisplayLength
        );
      }
    };
  }

  // No custom rendering needed
  return undefined;
}
